import type { PhotoInfo } from '@freealbum/document';
import type { PhotoSort, UploadItem } from './types';

/** A tile keeps its identity and position when a local file becomes a server photo. */
export interface PhotoTrayEntry {
  key: string;
  photoId?: string;
  uploadId?: string;
  name?: string;
}

export interface PhotoTrayState {
  photos: Record<string, PhotoInfo>;
  photoIds: string[];
  photoTray: PhotoTrayEntry[];
  uploads: Record<string, UploadItem>;
}

/** Active transfers cannot be removed while their callbacks still own the tile. */
export function canSelectTrayEntry(state: Pick<PhotoTrayState, 'photos' | 'uploads'>, entry: PhotoTrayEntry): boolean {
  const upload = entry.uploadId ? state.uploads[entry.uploadId] : undefined;
  if (upload) return upload.status === 'error';
  const status = entry.photoId ? state.photos[entry.photoId]?.status : undefined;
  return status === 'ready' || status === 'failed' || status === 'uploading';
}

function samePhoto(a: PhotoInfo | undefined, b: PhotoInfo): boolean {
  return Boolean(a && a.id === b.id && a.name === b.name && a.bytes === b.bytes && a.mime === b.mime && a.status === b.status && a.width === b.width && a.height === b.height
    && a.takenAt === b.takenAt && a.contributorName === b.contributorName && a.source === b.source && sameItems(a.tags, b.tags)
    && a.urls.thumb === b.urls.thumb && a.urls.preview === b.urls.preview && a.urls.original === b.urls.original
    && a.flags.blurry === b.flags.blurry && a.flags.duplicateOf === b.flags.duplicateOf);
}

function sameItems<T>(a: T[], b: T[]): boolean {
  return a.length === b.length && a.every((item, index) => item === b[index]);
}

/**
 * A photo as the editor relies on it. Fields added after an album was first cached — a stale PWA
 * cache, a fixture written earlier — arrive missing rather than empty, and the tray reads them.
 */
export function normalizePhoto(photo: PhotoInfo): PhotoInfo {
  const raw = photo as Partial<PhotoInfo> & Pick<PhotoInfo, 'id'>;
  if (Array.isArray(raw.tags) && raw.source && raw.flags) return photo;
  return {
    ...photo,
    tags: Array.isArray(raw.tags) ? raw.tags : [],
    source: raw.source ?? 'upload',
    flags: raw.flags ?? { duplicateOf: null, blurry: false },
  };
}

/** Poll responses may be in a different order, and contain freshly allocated but unchanged objects. */
export function mergeTrayPhotos(state: PhotoTrayState, incoming: PhotoInfo[], replace = true) {
  const photos = replace ? {} as Record<string, PhotoInfo> : { ...state.photos };
  for (const raw of incoming) {
    const photo = normalizePhoto(raw);
    photos[photo.id] = samePhoto(state.photos[photo.id], photo) ? state.photos[photo.id]! : photo;
  }
  // A poll started before a completion response must not remove its newly inserted photo.
  for (const upload of Object.values(state.uploads)) {
    if (upload.photoId && !photos[upload.photoId] && state.photos[upload.photoId]) photos[upload.photoId] = state.photos[upload.photoId]!;
  }
  const photoIds = state.photoIds.filter((id) => photos[id]);
  const knownIds = new Set(photoIds);
  for (const photo of incoming) if (!knownIds.has(photo.id)) { photoIds.push(photo.id); knownIds.add(photo.id); }
  const photoTray = state.photoTray.filter((entry) => (entry.uploadId && state.uploads[entry.uploadId]) || (entry.photoId && photos[entry.photoId]));
  const represented = new Set(photoTray.map((entry) => entry.photoId));
  for (const id of photoIds) if (!represented.has(id)) photoTray.push({ key: `photo:${id}`, photoId: id, name: photos[id]?.name ?? undefined });
  return {
    photos: sameItems(state.photoIds, photoIds) && photoIds.every((id) => state.photos[id] === photos[id]) ? state.photos : photos,
    photoIds: sameItems(state.photoIds, photoIds) ? state.photoIds : photoIds,
    photoTray: sameItems(state.photoTray, photoTray) ? state.photoTray : photoTray,
  };
}

export function queueTrayUpload(state: PhotoTrayState, item: UploadItem) {
  const existing = state.photoTray.find((entry) => entry.uploadId === item.id);
  let photoTray = state.photoTray;
  if (!existing) {
    const reserved = item.photoId ? photoTray.find((entry) => entry.photoId === item.photoId) : undefined;
    photoTray = reserved
      ? photoTray.map((entry) => entry === reserved ? { ...entry, uploadId: item.id, name: item.name } : entry)
      : [...photoTray, { key: `upload:${item.id}`, uploadId: item.id, photoId: item.photoId, name: item.name }];
  } else if (existing.photoId !== item.photoId) {
    photoTray = photoTray.map((entry) => entry === existing ? { ...entry, photoId: item.photoId } : entry);
  }
  // Linking a file also absorbs a server row that polling may have discovered first.
  if (item.photoId && photoTray.some((entry) => entry.photoId === item.photoId && entry.uploadId !== item.id)) {
    photoTray = photoTray.filter((entry) => entry.photoId !== item.photoId || entry.uploadId === item.id);
  }
  return { uploads: { ...state.uploads, [item.id]: item }, photoTray };
}

/** Sorting is a deliberate action, never a side effect of processing EXIF metadata. */
export function sortPhotoTray(entries: PhotoTrayEntry[], photos: Record<string, PhotoInfo>, sort: PhotoSort) {
  return [...entries].sort((a, b) => {
    if (sort === 'name') return (a.name ?? '').localeCompare(b.name ?? '', undefined, { numeric: true });
    const date = (entry: PhotoTrayEntry) => {
      const takenAt = entry.photoId ? photos[entry.photoId]?.takenAt : null;
      const parsed = takenAt ? Date.parse(takenAt) : NaN;
      return Number.isNaN(parsed) ? Infinity : parsed;
    };
    return date(a) - date(b) || 0;
  });
}
